import api from '@/services/Api';
import {errorSwal, successSwal} from '@/helper/swals';
import { ActionContext, Module } from 'vuex';
import { AxiosResponse } from 'axios';
import { Terminkalender, Termin, Termindauer, Terminplatz, Terminzeit } from '@/types/Terminkalender';
import {RootState, TerminkalenderState, TerminSucheFilter, UebertragungStatus} from '@/store/types';
import { DataResponse } from '@/types';
import Terminart from '@/types/Terminart';
import moment from 'moment';
import {saveAs} from 'file-saver';
import {AnmeldungStatus} from '@/types/AnmeldungStatus';
import { SystemEinstellung, SystemEinstellungKey } from '@/types/Einstellung';

export type UpdateAnmeldungPayload = { anmeldung: Termin };
export type LoadTerminkalenderData = DataResponse<Partial<Termin>[], {
    arten: Partial<Terminart>[],
    dauer: Partial<Termindauer>[],
    plaetze: Partial<Terminplatz>[],
    zeiten: Partial<Terminzeit>[],
}>;
export type SetTerminkalenderLoaders = { save?: boolean; delete?: boolean; termine?: boolean; anmeldungen?: boolean; print?: boolean; export?: boolean };
export type SetTerminkalenderFilter = { von: string; bis: string;};
export type TerminkalenderanmeldungenFilter = {
    zeitraum: [string, string];
    zeitraum_gebucht: [string, string];
    status?: AnmeldungStatus;
    name: string
};
export type SetSearchPayload = { termine?: Termin[]; filter?: TerminSucheFilter };
export type ExportSource = 'anforderungen' | 'titel' | 'termine';
export type ExportSyncPayload = { source: ExportSource };

type Context = ActionContext<TerminkalenderState, RootState>;

export const terminkalender: Module<TerminkalenderState, RootState> = {
    namespaced: true,
    state: {
        day: '',
        type: 'week',
        termin: new Termin(),
        kalender: new Terminkalender(),
        anmeldungen: [],
        dialog: false,
        anmeldungDialog: false,
        searchDialog: false,
        loaders: {
            save: false,
            delete: false,
            deleteterminserie: false,
            termine: false,
            suche: false,
            anmeldungen: false,
            sync: false,
        },
        filter: null,
        filterAnmeldung: {
            zeitraum: [
                moment().subtract(1, 'month').format('YYYY-MM-DD'),
                moment().add(1, 'month').format('YYYY-MM-DD'),
            ],
            zeitraum_gebucht: [
                moment().subtract(1, 'month').format('YYYY-MM-DD'),
                moment().add(1, 'month').format('YYYY-MM-DD'),
            ],
            status: AnmeldungStatus.Verarbeitet,
            name: '',
        },
        suche: {
            termine: [],
            filter: {
                von: moment().format('YYYY-MM-DD'),
                bis: '',
                zeit: '',
                dauer: '',
                platznr: '',
                terminart: '',
                erschienen: '',
                patient: '',
                hinweis: '',
            },
        },
    },
    actions: {
        loadKalender({ commit, state: { filter } }: Context): void {
            commit('setLoaders', { termine: true });
            //commit('clearTermine');

            api.post('terminkalender/terminkalender/load', { filter })
                .then((response: AxiosResponse) => commit('setKalender', response.data))
                .catch(console.error)
                .finally(() => commit('setLoaders', { termine: false }));
        },
        loadTerminanmeldungen({ commit }, filter: TerminkalenderanmeldungenFilter): void {
            commit('setLoaders', { anmeldungen: true });

            api.post('terminkalender/terminkalender/loadAnmeldungen', { filter })
                .then((response: AxiosResponse) => commit('setAnmeldungen', response.data))
                .catch(console.error)
                .finally(() => commit('setLoaders', { anmeldungen: false }));
        },
        searchKalender({ commit }: Context, filter: TerminSucheFilter): void {
            commit('setLoaders', { suche: true });
            commit('setSearch', { filter });

            api.post('terminkalender/terminkalender/search', { filter })
                .then((response: AxiosResponse) => {
                    const termine = response.data.body.map((termin: Partial<Termin>) => new Termin(termin));
                    commit('setSearch', { termine });
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { suche: false }));
        },
        updateTermin({ commit, dispatch }: Context, payload: Termin): void {
            commit('setLoaders', { save: true });

            api.post('terminkalender/terminkalender/speichern', payload)
                .then(() => {
                    successSwal('Termin', 'gespeichert');
                    dispatch('loadKalender');
                    dispatch('exportSync', { source: 'termine' });
                    commit('setDialog', false);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { save: false }));
        },
        deleteTermin({ commit, dispatch }: Context, payload: Termin): void {
            commit('setLoaders', { delete: true });

            api.post('terminkalender/terminkalender/loeschen', payload)
                .then(() => {
                    successSwal('Termin', 'gelöscht');
                    dispatch('loadKalender');
                    dispatch('exportSync', { source: 'termine' });
                    commit('setDialog', false);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { delete: false }));
        },
        deleteTerminserie({ commit, dispatch }: Context, payload: Termin): void {
            commit('setLoaders', { deleteterminserie: true });

            api.post('terminkalender/terminkalender/serieloeschen', payload)
                .then(() => {
                    successSwal('Terminserie', 'gelöscht');
                    dispatch('loadKalender');
                    dispatch('exportSync', { source: 'termine' });
                    commit('setDialog', false);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { deleteterminserie: false }));
        },
        printTermine({ commit, dispatch }: Context, payload): void {
            commit('setLoaders', { print: true });

            api.post('terminkalender/terminkalenderdruck', { termine: payload.termine, filter: payload.filter }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setLoaders', { print: false }));
        },
        exportTermine({ commit, dispatch }: Context, payload): void {
            commit('setLoaders', { export: true });
            api.post('terminkalender/terminkalenderdruck/export', { termine: payload.termine, filter: payload.filter }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    if (response.data.type === 'text/html') {
                        response.data.text().then((text: string) => {
                            const data = JSON.parse(text);
                            errorSwal('Terminkalender', data.body);
                        });
                    } else {
                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        saveAs(response.data, `Termineübersicht_${date}.xlsx`);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { export: false }));
        },
        syncAnmeldungen({ commit, dispatch }: Context, filter: TerminkalenderanmeldungenFilter): void {
            commit('setLoaders', { sync: true });

            api.post('terminkalender/sync/import')
                .then(() => {
                    successSwal('Anmeldungen', 'synchronisiert');
                    dispatch('loadTerminanmeldungen', filter);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { sync: false }));
        },
        exportSync({ rootGetters }: Context, payload: ExportSyncPayload): void {
            const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                rootGetters['authentication/getSystemEinstellung'];

            if (getSystemEinstellung('system_termine').isEnabled()) {
                api.post('terminkalender/sync/export', payload)
                    .catch(console.error);
            }
        },
        updateAnmeldung({ commit, dispatch }: Context, payload: Termin): void {
            commit('setLoaders', { save: true });

            api.post('terminkalender/terminkalender/saveanmeldung', payload)
                .then(() => {
                    successSwal('Anmeldung', 'gespeichert');
                    dispatch('loadTerminanmeldungen');
                    commit('setAnmeldungDetail', false);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { save: false }));
        },
    },
    mutations: {
        setDay(state: TerminkalenderState, day: string): void {
            state.day = day;
        },
        setType(state: TerminkalenderState, type: string): void {
            state.type = type;
        },
        setTermin(state: TerminkalenderState, termine: Termin): void {
            state.termin = termine;
        },
        clearTermine(state: TerminkalenderState): void {
            state.kalender.termine = [];
        },
        setKalender(state: TerminkalenderState, { body, selections }: LoadTerminkalenderData): void {
            state.kalender = new Terminkalender();
            state.kalender.termine = body.map((data) => new Termin(data));
            state.kalender.arten = selections.arten.map((data) => new Terminart(data));
            state.kalender.dauer = selections.dauer.map((data) => new Termindauer(data));
            state.kalender.plaetze = selections.plaetze.map((data) => new Terminplatz(data));
            state.kalender.zeiten = selections.zeiten.map((data) => new Terminzeit(data));
        },
        setAnmeldungen(state: TerminkalenderState, { body }: LoadTerminkalenderData): void {
            state.anmeldungen = body.map((data) => new Termin(data));
        },
        setDialog(state: TerminkalenderState, visible: boolean): void {
            state.dialog = visible;
        },
        setSearchDialog(state: TerminkalenderState, visible: boolean): void {
            state.searchDialog = visible;
        },
        setLoaders(state: TerminkalenderState, loaders: SetTerminkalenderLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...loaders,
            };
        },
        setSearch(state: TerminkalenderState, payload: SetSearchPayload): void {
            state.suche = {
                ...state.suche,
                ...payload,
            };
        },
        setFilter(state: TerminkalenderState, filter: SetTerminkalenderFilter): void {
            state.filter = filter;
        },

        setAnmeldungDetail(state: TerminkalenderState, visible: boolean): void {
            state.anmeldungDialog = visible;
        },

    },
};
